import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowRight, 
  ArrowLeft,
  GraduationCap, 
  Building2, 
  Briefcase, 
  HelpCircle,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Linkedin,
  Mail,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  // Form data for each intent type
  const [studentForm, setStudentForm] = useState({
    firstName: '',
    email: '',
    whatsapp: '',
    message: '',
  });

  const [collegeForm, setCollegeForm] = useState({
    institution_name: '',
    contact_person: '',
    designation: '',
    official_email: '',
    phone: '',
  });

  const [employerForm, setEmployerForm] = useState({
    company_name: '',
    contact_person: '',
    work_email: '',
    roles_hiring: '',
    phone: '',
  });

  const [generalForm, setGeneralForm] = useState({
    name: '',
    email: '',
    message: '',
  });

  // Intent options
  const intents = [
    {
      id: 'student',
      title: "I'm a Student",
      description: "Career guidance, readiness or support",
      icon: GraduationCap,
      color: "indigo",
    },
    {
      id: 'college',
      title: "I represent a College",
      description: "Student readiness, employability or partnerships",
      icon: Building2,
      color: "purple",
    },
    {
      id: 'employer',
      title: "I'm an Employer",
      description: "Hiring, talent access or campus engagement",
      icon: Briefcase,
      color: "blue",
    },
    {
      id: 'general',
      title: "Something Else",
      description: "General questions or other enquiries",
      icon: HelpCircle,
      color: "slate",
    },
  ];

  // Track intent selection
  const handleIntentSelect = (intentId) => {
    setSelectedIntent(intentId);
    setError('');
    setSubmitSuccess(false);

    // Track analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', `contact_${intentId}_selected`);
    }
  };

  // Handle form submissions
  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // For students, we'll use a general contact endpoint or create a specific one
      const response = await fetch(`${API_URL}/api/contact/student`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(studentForm),
      });

      if (!response.ok) {
        throw new Error('Failed to submit. Please try again.');
      }

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'student_contact_submitted');
      }
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCollegeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/college-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(collegeForm),
      });

      if (!response.ok) {
        throw new Error('Failed to submit. Please try again.');
      }

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'college_contact_submitted');
      }
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmployerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/employer-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employerForm),
      });

      if (!response.ok) {
        throw new Error('Failed to submit. Please try again.');
      }

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'employer_contact_submitted');
      }
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/contact/general`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(generalForm),
      });

      if (!response.ok) {
        throw new Error('Failed to submit. Please try again.');
      }

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'general_contact_submitted');
      }
      setSubmitSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedIntent(null);
    setSubmitSuccess(false);
    setError('');
    setStudentForm({ firstName: '', email: '', whatsapp: '', message: '' });
    setCollegeForm({ institution_name: '', contact_person: '', designation: '', official_email: '', phone: '' });
    setEmployerForm({ company_name: '', contact_person: '', work_email: '', roles_hiring: '', phone: '' });
    setGeneralForm({ name: '', email: '', message: '' });
  };

  // Success state
  if (submitSuccess) {
    return (
      <div data-testid="contact-page" className="min-h-screen bg-white">
        <section className="py-20 lg:py-32">
          <div className="max-w-md mx-auto px-4 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Message Sent!</h1>
            <p className="text-slate-600 mb-8">
              Thank you for reaching out. We'll get back to you soon.
            </p>
            <Button onClick={resetForm} variant="outline" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Send Another Message
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="min-h-screen bg-white">
      {/* ============================================
          HERO - Minimal
          ============================================ */}
      <section className="bg-[#0b1f4d] text-white pt-24 sm:pt-20 lg:pt-0 py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-widest text-teal-300 mb-4">
            CONTACT ELEVATE CAREERS
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            How can we help?
          </h1>
          <p className="text-lg text-slate-400">
            Choose what you'd like to speak with us about.
          </p>
        </div>
      </section>

      {/* ============================================
          INTENT SELECTOR / DYNAMIC FORM
          ============================================ */}
      <section className="py-12 lg:py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {!selectedIntent ? (
            /* Intent Selector Cards */
            <div className="grid sm:grid-cols-2 gap-4">
              {intents.map((intent) => (
                <button
                  key={intent.id}
                  onClick={() => handleIntentSelect(intent.id)}
                  className={`text-left bg-white border-2 rounded-2xl p-6 transition-all hover:shadow-lg ${
                    intent.color === 'indigo' ? 'border-slate-200 hover:border-teal-300' :
                    intent.color === 'purple' ? 'border-slate-200 hover:border-teal-300' :
                    intent.color === 'blue' ? 'border-slate-200 hover:border-blue-300' :
                    'border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`intent-${intent.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    intent.color === 'indigo' ? 'bg-teal-50' :
                    intent.color === 'purple' ? 'bg-teal-100' :
                    intent.color === 'blue' ? 'bg-blue-100' :
                    'bg-slate-100'
                  }`}>
                    <intent.icon className={`w-6 h-6 ${
                      intent.color === 'indigo' ? 'text-teal-700' :
                      intent.color === 'purple' ? 'text-teal-600' :
                      intent.color === 'blue' ? 'text-blue-600' :
                      'text-slate-600'
                    }`} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{intent.title}</h3>
                  <p className="text-slate-500 text-sm">{intent.description}</p>
                </button>
              ))}
            </div>
          ) : (
            /* Dynamic Form based on intent */
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
              {/* Back button */}
              <button
                onClick={() => setSelectedIntent(null)}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to options
              </button>

              {/* Student Form */}
              {selectedIntent === 'student' && (
                <>
                  <h2 className="text-xl font-bold text-slate-900 mb-6">Student Query</h2>
                  <form onSubmit={handleStudentSubmit} className="space-y-4">
                    <Input
                      type="text"
                      placeholder="First Name"
                      value={studentForm.firstName}
                      onChange={(e) => setStudentForm(prev => ({ ...prev, firstName: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={studentForm.email}
                      onChange={(e) => setStudentForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <Input
                      type="tel"
                      placeholder="WhatsApp Number"
                      value={studentForm.whatsapp}
                      onChange={(e) => setStudentForm(prev => ({ ...prev, whatsapp: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <Textarea
                      placeholder="Message (optional)"
                      value={studentForm.message}
                      onChange={(e) => setStudentForm(prev => ({ ...prev, message: e.target.value }))}
                      rows={3}
                      className="rounded-xl"
                    />

                    {error && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] py-6 rounded-full font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send My Query
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>

                  {/* Alternative CTA for students */}
                  <div className="mt-6 pt-6 border-t border-slate-200 text-center">
                    <Link
                      to="/career-readiness-check"
                      className="text-sm text-teal-700 hover:text-teal-700 font-medium"
                    >
                      Not sure what you need? Find My Career Gap — Free →
                    </Link>
                  </div>
                </>
              )}

              {/* College Form */}
              {selectedIntent === 'college' && (
                <>
                  <h2 className="text-xl font-bold text-slate-900 mb-6">College Partnership Inquiry</h2>
                  <form onSubmit={handleCollegeSubmit} className="space-y-4">
                    <Input
                      type="text"
                      placeholder="Institution Name"
                      value={collegeForm.institution_name}
                      onChange={(e) => setCollegeForm(prev => ({ ...prev, institution_name: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        type="text"
                        placeholder="Your Name"
                        value={collegeForm.contact_person}
                        onChange={(e) => setCollegeForm(prev => ({ ...prev, contact_person: e.target.value }))}
                        required
                        className="rounded-xl h-12"
                      />
                      <Input
                        type="text"
                        placeholder="Designation"
                        value={collegeForm.designation}
                        onChange={(e) => setCollegeForm(prev => ({ ...prev, designation: e.target.value }))}
                        required
                        className="rounded-xl h-12"
                      />
                    </div>
                    <Input
                      type="email"
                      placeholder="Official Email"
                      value={collegeForm.official_email}
                      onChange={(e) => setCollegeForm(prev => ({ ...prev, official_email: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <Input
                      type="tel"
                      placeholder="Phone Number"
                      value={collegeForm.phone}
                      onChange={(e) => setCollegeForm(prev => ({ ...prev, phone: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />

                    {error && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 rounded-full font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Explore a Partnership
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}

              {/* Employer Form */}
              {selectedIntent === 'employer' && (
                <>
                  <h2 className="text-xl font-bold text-slate-900 mb-6">Employer Hiring Inquiry</h2>
                  <form onSubmit={handleEmployerSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        type="text"
                        placeholder="Company Name"
                        value={employerForm.company_name}
                        onChange={(e) => setEmployerForm(prev => ({ ...prev, company_name: e.target.value }))}
                        required
                        className="rounded-xl h-12"
                      />
                      <Input
                        type="text"
                        placeholder="Your Name"
                        value={employerForm.contact_person}
                        onChange={(e) => setEmployerForm(prev => ({ ...prev, contact_person: e.target.value }))}
                        required
                        className="rounded-xl h-12"
                      />
                    </div>
                    <Input
                      type="email"
                      placeholder="Work Email"
                      value={employerForm.work_email}
                      onChange={(e) => setEmployerForm(prev => ({ ...prev, work_email: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <Input
                      type="text"
                      placeholder="Role / Hiring Need"
                      value={employerForm.roles_hiring}
                      onChange={(e) => setEmployerForm(prev => ({ ...prev, roles_hiring: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <Input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={employerForm.phone}
                      onChange={(e) => setEmployerForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="rounded-xl h-12"
                    />

                    {error && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-full font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Discuss Hiring Needs
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}

              {/* General Form */}
              {selectedIntent === 'general' && (
                <>
                  <h2 className="text-xl font-bold text-slate-900 mb-6">General Enquiry</h2>
                  <form onSubmit={handleGeneralSubmit} className="space-y-4">
                    <Input
                      type="text"
                      placeholder="Name"
                      value={generalForm.name}
                      onChange={(e) => setGeneralForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={generalForm.email}
                      onChange={(e) => setGeneralForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="rounded-xl h-12"
                    />
                    <Textarea
                      placeholder="Message"
                      value={generalForm.message}
                      onChange={(e) => setGeneralForm(prev => ({ ...prev, message: e.target.value }))}
                      required
                      rows={4}
                      className="rounded-xl"
                    />

                    {error && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#0b1f4d] hover:bg-[#13296b] text-white py-6 rounded-full font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowRight className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          )}

          {/* WhatsApp alternative - subtle */}
          <div className="mt-8 text-center">
            <a
              href="https://wa.me/919311510040?text=Hi%20Elevate%20Careers"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Prefer WhatsApp? Chat with us →
            </a>
          </div>
        </div>
      </section>

      {/* ============================================
          CONTACT DETAILS - Minimal
          ============================================ */}
      <section className="py-12 bg-white">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a
              href="mailto:team@elevatecareersindia.in"
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
            >
              <Mail className="w-4 h-4" />
              team@elevatecareersindia.in
            </a>
            <a
              href="https://www.linkedin.com/company/elevate-careers-india"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
            >
              <Linkedin className="w-4 h-4" />
              LinkedIn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
